import type model from './model';
import type { TFunction, IWixWindow, Experiments } from '@wix/yoshi-flow-editor';
import { ITEM_MODAL_COMPONENT_IDS } from '../../appConsts/blocksIds';
import { buildImgSrc, getAltText } from '../Dishes/utils';
import type {
  ModifierListItem,
  ModifierRepeaterData,
  RepeaterModifierGroupListItem,
} from '../../types/modifiers';
import type { DishModalProps } from '../../types/widgetsProps';
import type { ItemData } from '../../types/item';
import type {
  ADD_TO_CART_ERRORS,
  CartLineItem,
  CatalogReferencePriceVariant,
} from '../../services/cartService';
import { sumAdditionalCharges } from '../../utils/modifiersUtils';
import { getVariantById, getVariantWithMinimalPrice } from '../../utils/priceVariantsUtils';
import { MODIFIER_RULE_STATES } from '../../types/businessTypes';
import { state, DEFAULT_QUANTITY } from '../../states/ItemModalState';
import type { ControllerParams } from '../../types/widgets';
import {
  getFirstPreSelectedModifier,
  getModifierGroupErrorText,
  getModifierGroupLabel,
  getPreSelectedModifiers,
  isSelectedModifierGroupsValid,
  isSingleSelectRule,
} from 'root/utils/modifierGroupUtils';
import {
  getAreNotEnoughModifiersOfMandatoryModifierGroupInStock,
  getModifierGroupsOptions,
  getModifierListItem,
  getModifiersOptions,
} from 'root/utils/itemModalControllerUtils';
import * as itemModalControllerUtils from 'root/utils/itemModalControllerUtils';
import type { PriceFormatter } from '@wix/restaurants-olo-operations-client-commons';
import type { LabelWithSvg } from 'root/types/label';
import type {
  DisplayedModifier,
  DisplayedPriceVariant,
  ItemImage,
} from '@wix/restaurants-populated-item-client/types';
import {
  LiveSiteClickFulfillmentOrigin,
  LiveSiteMenuStatusCardDisplayedPageName,
} from '@wix/restaurants-bi';
import { getRelativePath } from 'root/utils/urlUtils';
import { SPECS } from 'root/appConsts/experiments';

type BindAll = ControllerParams<typeof model>['$bindAll'];
type I$W = ControllerParams<typeof model>['$w'];

export class ItemModalController {
  private formatCurrency: (price?: number) => string;

  constructor(
    private $bindAll: BindAll,
    private $w: I$W,
    private t: TFunction,
    priceFormatter: PriceFormatter,
    private window: IWixWindow,
    private isEditor: boolean,
    private experiments: Experiments,
    private isMemberLoggedIn: boolean,
    private pageUrl: string
  ) {
    this.formatCurrency = (price?: number) => {
      return price !== undefined ? priceFormatter(price) : '';
    };
  }

  initEditItem = (
    cartItem: CartLineItem,
    priceVariants: DisplayedPriceVariant[],
    itemModifierGroups: RepeaterModifierGroupListItem[]
  ) => {
    const { quantity, options } = cartItem;
    const { specialRequests, priceVariant, modifierGroups } = options ?? {};
    state.quantity = quantity || DEFAULT_QUANTITY;
    state.specialRequests = specialRequests ?? '';
    if (priceVariant?.id) {
      const selectedPriceVariant = getVariantById({
        priceVariantsInItem: priceVariants,
        variantId: priceVariant.id,
      });
      if (selectedPriceVariant) {
        state.selectedPriceVariant = selectedPriceVariant;
      }
    }

    if (modifierGroups && itemModifierGroups) {
      modifierGroups.forEach((modifierGroup) => {
        const selectedModifiersIds = modifierGroup.modifiers.map((m) => m.id);
        const selectedModifierGroup = itemModifierGroups?.find(
          (_modifierGroup) => _modifierGroup?.modifierGroupId === modifierGroup?.id
        );
        const selectedModifiers = selectedModifierGroup?.modifiers.filter((modifier) =>
          selectedModifiersIds.includes(modifier.modifierId ?? '')
        );
        selectedModifierGroup &&
          selectedModifiers &&
          this.setSelectedModifiers(selectedModifierGroup?.id, selectedModifiers);
      });
    }
  };

  get firstItemToFocus() {
    return this.$w(ITEM_MODAL_COMPONENT_IDS.close);
  }

  setFocusedItem() {
    setTimeout(() => {
      (this.firstItemToFocus as unknown as HTMLElement)?.focus();
    }, 500);
  }

  init(
    dishModalProps: DishModalProps,
    onModalOpen: () => void = () => {},
    closeModal: (
      window: IWixWindow,
      dataPromise: Promise<{ cartItem?: CartLineItem; error?: ADD_TO_CART_ERRORS }> | undefined,
      additionalData?: { cartLineItem?: CartLineItem; isUpdate?: boolean }
    ) => void = () => {},
    modifierGroups: RepeaterModifierGroupListItem[]
  ) {
    const {
      item,
      cartService,
      biReporterService,
      operation,
      canAcceptOrders,
      cartItem,
      menuId,
      sectionId,
      availabilityStatusProps = {},
      shouldAddItemToCart,
      openDispatchModal,
    } = dishModalProps;
    const {
      isMenuOfItemAvailable = true,
      shouldCollapseAvailabilityStatus,
      dispatchType,
      text,
      hasNextAvailability,
    } = availabilityStatusProps;

    this.setFocusedItem();

    if (item) {
      state.amount = item.price.amount;
      state.currency = item.price.currency;
      const areNotEnoughModifiersOfMandatoryModifierGroupInStock =
        getAreNotEnoughModifiersOfMandatoryModifierGroupInStock(modifierGroups);
      const isInStockItem = item.orderSettings?.inStock ?? true;
      const isValidToContinue =
        canAcceptOrders &&
        isInStockItem &&
        isMenuOfItemAvailable &&
        !areNotEnoughModifiersOfMandatoryModifierGroupInStock;
      const itemPriceVariants = item.priceVariants?.variants || [];

      if (itemPriceVariants.length) {
        const variantWithMinimalPriceString = state.selectedPriceVariant.id
          ? state.selectedPriceVariant.priceInfo.price
          : getVariantWithMinimalPrice(itemPriceVariants)?.priceInfo.price;
        const variantWithMinimalPrice = Number(variantWithMinimalPriceString || 0);
        state.amount = variantWithMinimalPrice;
        state.basePrice = variantWithMinimalPrice;
      }

      const hasImageUrl = !!item.image?.url;

      const imageProps = hasImageUrl
        ? {
            type: 'image',
            src: item.image && (buildImgSrc(item.image) as string),
            alt: getAltText({ itemName: item.name, t: this.t }),
          }
        : { type: 'image', src: '' };
      const acceptSpecialRequests = item.orderSettings?.acceptSpecialRequests;

      const hasLabels = item.labels?.length;
      const hasPriceVariants = Boolean(itemPriceVariants?.length);
      const hasModifierGroups = Boolean(modifierGroups?.length);

      hasLabels && this.initLabels(item);
      itemPriceVariants?.length && this.initPriceVariants(itemPriceVariants);

      item.modifierGroups?.length && this.initModifiers(modifierGroups);

      if (cartItem) {
        this.initEditItem(cartItem, itemPriceVariants, modifierGroups);
      }

      const { id: operationId, operationGroupId, locationId, locationDetails } = operation || {};

      this.$w(ITEM_MODAL_COMPONENT_IDS.counterInput)?.hideNumberSpinner();

      const isFeaturedItemExperimentEnabled = this.experiments.enabled(SPECS.featuredItemDesign);
      const isMultipleImagesExperimentEnabled = this.experiments.enabled(SPECS.multipleImages);
      state.images = isMultipleImagesExperimentEnabled
        ? [item?.image, ...(item?.additionalImages ?? [])].filter(
            (img): img is ItemImage => img != null && img.url !== undefined
          )
        : [];

      this.$bindAll({
        [ITEM_MODAL_COMPONENT_IDS.counterContainer]: {
          collapsed: () => !isValidToContinue,
        },
        [ITEM_MODAL_COMPONENT_IDS.counterInput]: {
          value: () => state.quantity.toString(),
          placeholder: () => '',
          onInput: (evt: $w.Event) => (state.quantity = Number(evt.target.value)),
          onChange: (evt: $w.Event) => {
            const quantity = Number(evt.target.value);
            state.quantity = quantity < 1 ? DEFAULT_QUANTITY : quantity;
          },
        },
        [ITEM_MODAL_COMPONENT_IDS.increaseButton]: {
          collapsed: () => !isValidToContinue,
          onClick: () => state.quantity++,
          label: () => this.t('itemModal.increaseButton.label'),
        },
        [ITEM_MODAL_COMPONENT_IDS.decreaseButton]: {
          collapsed: () => !isValidToContinue,
          disabled: () => state.quantity === 1,
          onClick: () => state.quantity--,
          label: () => this.t('itemModal.decreaseButton.label'),
        },
        [ITEM_MODAL_COMPONENT_IDS.title]: {
          text: () => item.name || '',
        },
        [ITEM_MODAL_COMPONENT_IDS.subTitle]: {
          text: () => item.description || '',
          collapsed: () => !item.description,
        },
        [ITEM_MODAL_COMPONENT_IDS.addToCart]: {
          onClick: async () => {
            const selectedModifierGroups = getModifierGroupsOptions(
              modifierGroups,
              state.selectedModifiers,
              this.formatCurrency
            );
            const shouldAddToCart = modifierGroups
              ? this.validateSelectedModifierGroups(modifierGroups)
              : true;

            this.updateModifiersElementValidationStatus();

            let cartResponsePromise;
            if (shouldAddToCart) {
              const options = {
                priceVariant: this.getPriceVariantOptions(state.selectedPriceVariant),
                modifierGroups: selectedModifierGroups,
                operationId,
                onlineOrderingPageUrl: getRelativePath(this.pageUrl),
                menuSectionId: sectionId,
                menuId,
                specialRequests: state.specialRequests !== '' ? state.specialRequests : undefined,
              };
              const cartLineItem = {
                catalogItemId: item._id ?? item.id,
                quantity: state.quantity,
                options,
              };
              if (shouldAddItemToCart) {
                cartResponsePromise = cartService?.addItemToCart(
                  cartLineItem,
                  cartItem?.id ?? undefined,
                  operation?.locationId
                );
              }
              closeModal(this.window, cartResponsePromise, { cartLineItem, isUpdate: !!cartItem });
            } else if (Object.values(state.modifiersErrors).some(Boolean)) {
              setTimeout(() => {
                this.$w(ITEM_MODAL_COMPONENT_IDS.multiSelect).focus();
                this.$w(ITEM_MODAL_COMPONENT_IDS.singleSelect).focus();
              }, 0);
            }
            const numSelectedModifiers =
              selectedModifierGroups?.reduce(
                (prevValue, currValue) => prevValue + (currValue.modifiers?.length || 0),
                0
              ) || 0;

            const value = await cartResponsePromise;

            biReporterService?.reportOloLiveSiteAddDishToCartBiEvent({
              itemName: item.name,
              itemId: item._id ?? item.id,
              menuId,
              sectionId,
              num_labels: item.labels?.length || 0,
              labelsList: JSON.stringify(item.labels || []),
              amountOfItems: state.quantity,
              numSelectedModifiers,
              priceVariationName: state.selectedPriceVariant.name,
              selectedModifiersList: JSON.stringify(selectedModifierGroups),
              itemPrice: state.amount * state.quantity,
              isSuccess: Boolean(!value?.error),
              operationId,
              operationGroup: operationGroupId,
              locationId,
              isDefaultLocation: locationDetails?.default,
            });
          },
          label: () =>
            this.getAddToCartButtonText(
              item,
              state.quantity,
              state.amount,
              state.currency,
              isValidToContinue,
              !!cartItem,
              isMenuOfItemAvailable,
              areNotEnoughModifiersOfMandatoryModifierGroupInStock
            ),
          disabled: () => !isValidToContinue,
        },
        [ITEM_MODAL_COMPONENT_IDS.close]: {
          label: () => this.t('itemModal.closeButton.label'),
          onClick: async () => closeModal(this.window, undefined),
        },
        [ITEM_MODAL_COMPONENT_IDS.image]: {
          src: () => imageProps.src ?? '',
          alt: () => imageProps.alt ?? '',
          collapsed: () => {
            !hasImageUrl && onModalOpen();
            return !hasImageUrl;
          },
          onViewportEnter: () => {
            hasImageUrl && onModalOpen();
          },
        },
        [ITEM_MODAL_COMPONENT_IDS.nextImage]: {
          onClick: () => {
            state.currentImageIndex = (state.currentImageIndex + 1) % state.images.length;
            this.changeImageDisplay();
          },
          hidden: () => !(isMultipleImagesExperimentEnabled && state.images.length > 1),
          // @ts-expect-error
          accessibility: {
            ariaAttributes: {
              ariaLabel: () => this.t('itemModal.nextImage.ariaLabel'),
            },
          },
        },
        [ITEM_MODAL_COMPONENT_IDS.previousImage]: {
          onClick: () => {
            state.currentImageIndex =
              (state.currentImageIndex - 1 + state.images.length) % state.images.length;
            this.changeImageDisplay();
          },
          hidden: () => !(isMultipleImagesExperimentEnabled && state.images.length > 1),
          // @ts-expect-error
          accessibility: {
            ariaAttributes: {
              ariaLabel: () => this.t('itemModal.previousImage.ariaLabel'),
            },
          },
        },
        [ITEM_MODAL_COMPONENT_IDS.featured]: {
          // @ts-expect-error
          accessibility: {
            ariaAttributes: {
              label: () => this.t('menu_olo.item.featured-icon-alt-text') ?? '',
            },
          },
          hidden: () => !isFeaturedItemExperimentEnabled || !item.featured,
          collapsed: () => !isFeaturedItemExperimentEnabled || !item.featured,
          deleted: () => this.isEditor && (!isFeaturedItemExperimentEnabled || !item.featured),
        },
        [ITEM_MODAL_COMPONENT_IDS.labelsContainer]: {
          hidden: () => !hasLabels,
          collapsed: () => !hasLabels,
        },
        [ITEM_MODAL_COMPONENT_IDS.dishStatus]: {
          collapsed: () => {
            if (!isMenuOfItemAvailable) {
              biReporterService?.reportOloMenuStatusCardDisplayedBiEvent({
                menuId,
                sectionId,
                itemId: item._id ?? item.id,
                isButtonDisplayed: hasNextAvailability,
                pageName: LiveSiteMenuStatusCardDisplayedPageName.DISH_MODAL,
                isCardDisplayed: !shouldCollapseAvailabilityStatus,
                operationGroup: operationGroupId,
                locationId,
                isDefaultLocation: locationDetails?.default,
              });
            }
            return isMenuOfItemAvailable || !!shouldCollapseAvailabilityStatus;
          },
          deleted: () => this.isEditor,
        },
        [ITEM_MODAL_COMPONENT_IDS.specialRequest]: {
          placeholder: () => this.t('itemModal.specialRequests.placeholder'),
          label: () => this.t('itemModal.specialRequests.label'),
          collapsed: () => !acceptSpecialRequests,
          disabled: () => !isValidToContinue,
          onChange: (evt: $w.Event) => (state.specialRequests = evt.target.value),
          value: !this.isEditor ? () => state.specialRequests : undefined,
        },
        [ITEM_MODAL_COMPONENT_IDS.quantityText]: {
          text: () => this.t('itemModal.quantity.text'),
        },
        [ITEM_MODAL_COMPONENT_IDS.priceVariantsSingleSelect]: {
          collapsed: () => !hasPriceVariants,
        },
        [ITEM_MODAL_COMPONENT_IDS.modifiersContainer]: {
          collapsed: () => !hasModifierGroups,
        },
      });
      if (!this.isEditor) {
        this.$bindAll({
          [ITEM_MODAL_COMPONENT_IDS.dishStatusText]: {
            text: () => {
              const canUpdateTimeText = hasNextAvailability
                ? this.t(
                    `itemModal.menuAvailabilityStatus.${dispatchType?.toLowerCase()}.canUpdateTime.text`
                  )
                : '';
              return `${text} ${canUpdateTimeText}`;
            },
          },
          [ITEM_MODAL_COMPONENT_IDS.dishStatusButton]: {
            label: () =>
              this.t(
                `itemModal.menuAvailabilityStatus.${dispatchType?.toLowerCase()}.canUpdateTime.button`
              ),
            collapsed: () => !hasNextAvailability,
            onClick: async () => {
              biReporterService?.reportOloLiveSiteClickOnFulfillmentBiEvent({
                origin: LiveSiteClickFulfillmentOrigin.CLICK_DISH_STATUS_BUTTON_DISH_MODAL,
                dispatchType,
                isMemberLoggedIn: this.isMemberLoggedIn,
                menuId,
              });
              await openDispatchModal();
            },
          },
        });
      }
      const numOfModifiers = modifierGroups?.reduce(
        (prevValue, currValue) => prevValue + (currValue.modifiers?.length || 0),
        0
      );
      biReporterService?.reportOloLiveSiteItemModalOpenedBiEvent({
        itemName: item.name,
        itemId: item._id ?? item.id,
        menuId,
        sectionId,
        minItemPrice: state.amount || 0,
        num_labels: item.labels?.length || 0,
        numModifiers: numOfModifiers || 0,
        numModifiersGroups: modifierGroups?.length || 0,
        numPriceVariations: itemPriceVariants?.length || 0,
        isMenuItemAvailable: isMenuOfItemAvailable,
        isItemInStock: isInStockItem,
        operationId,
        operationGroup: operationGroupId,
        locationId,
        isDefaultLocation: locationDetails?.default,
      });
    }
  }

  getFormattedPriceVariantLabel(name: string, price: string) {
    return `${name} (${price})`;
  }

  changeImageDisplay() {
    if (state.images[state.currentImageIndex].url) {
      this.$w(ITEM_MODAL_COMPONENT_IDS.image).src = state.images[state.currentImageIndex].url ?? '';
      this.$w(ITEM_MODAL_COMPONENT_IDS.image).alt =
        state.images[state.currentImageIndex].altText || '';
    }
  }

  getAddToCartButtonText(
    item: ItemData,
    quantity: number,
    amount: number,
    currency: string,
    isValidToContinue: boolean,
    editMode: boolean,
    isMenuOfItemAvailable: boolean,
    areModifiersOfMandatoryModifierGroupOutOfStock: boolean
  ): string {
    const isInStockItem =
      (item.orderSettings?.inStock && !areModifiersOfMandatoryModifierGroupOutOfStock) ?? true;
    const formattedPrice = this.formatCurrency(quantity * amount);

    if (!isMenuOfItemAvailable) {
      return this.t('itemModal.itemUnavailable.addToCartButton', {
        price: formattedPrice,
      });
    }

    if (!isInStockItem) {
      return this.t('itemModal.outOfStock.addToCartButton', {
        price: formattedPrice,
      });
    }

    if (!isValidToContinue) {
      return this.t('itemModal.notValid.addToCartButton', {
        price: formattedPrice,
      });
    }

    if (editMode) {
      return this.t('itemModal.editMode.addToCartButton', {
        price: formattedPrice,
      });
    }

    return this.t('itemModal.addToCartButton', { price: formattedPrice });
  }

  async initLabels(item: ItemData) {
    const labelsWithSvg = await itemModalControllerUtils.getLabelsWithSvgElement(item.labels);
    this.$bindAll({
      [ITEM_MODAL_COMPONENT_IDS.labelsContainer]: {
        // @ts-expect-error
        data: () => labelsWithSvg,
        // @ts-expect-error
        item: (label: LabelWithSvg, bindItem) => {
          const { svgId } = label;
          const iconSrc = svgId ? { icon: () => ({ svgId }) } : {};
          bindItem(ITEM_MODAL_COMPONENT_IDS.label, {
            ...iconSrc,
            text: () => label.name,
            collapsed: () => !svgId && !label.name,
          });
        },
      },
    });
  }

  getSelectedPriceVariantIndex = (
    priceVariants: DisplayedPriceVariant[],
    selectedPriceVariant: string
  ) => {
    const selectedIndex = priceVariants.findIndex(({ id }) => id === selectedPriceVariant);
    return selectedIndex > 0 ? selectedIndex : 0;
  };

  initPriceVariants(priceVariants: DisplayedPriceVariant[]) {
    if (!state.selectedPriceVariant.id) {
      state.selectedPriceVariant = priceVariants && getVariantWithMinimalPrice(priceVariants);
    }
    const priceVariantsOptions = priceVariants.map((priceVariant) => {
      const formattedPrice = this.formatCurrency(Number(priceVariant.priceInfo.price));
      const label = this.getFormattedPriceVariantLabel(priceVariant.name ?? '', formattedPrice);
      return {
        label,
        value: priceVariant.id,
      };
    });
    const currentPriceVariantElement = ITEM_MODAL_COMPONENT_IDS.priceVariantsSingleSelect;

    this.$bindAll({
      [currentPriceVariantElement]: {
        collapsed: () => !priceVariants,
      },
    });

    if (priceVariants) {
      this.$bindAll({
        [currentPriceVariantElement]: {
          options: () => priceVariantsOptions,
          label: () => this.t('menu-olo.item-modal.price-variants'),
          required: () => true,
          onChange: (event: $w.Event) => {
            state.selectedPriceVariant =
              priceVariants.find((priceVariant) => {
                return priceVariant.id === event.target.value;
              }) ?? ({} as DisplayedPriceVariant);
            const price = priceVariants.find((priceVariant) => {
              return priceVariant.id === state.selectedPriceVariant.id;
            })?.priceInfo.price;
            state.amount = state.additionalCharge + Number(price);
            state.basePrice = Number(price);
          },
          selectedIndex: () =>
            this.getSelectedPriceVariantIndex(priceVariants, state.selectedPriceVariant.id ?? ''),
        },
      });
    }
  }

  calculateAddToCartButtonPrice() {
    const sum = sumAdditionalCharges(state.selectedModifiers);
    state.amount = state.amount - state.additionalCharge + sum;
    state.additionalCharge = sum;
  }

  setSelectedModifiers(modifierGroupId: string, modifierListItems: ModifierListItem[]) {
    state.selectedModifiers = {
      ...state.selectedModifiers,
      [modifierGroupId]: modifierListItems,
    };
    this.calculateAddToCartButtonPrice();
  }

  setDisabledModifiers(disabledModifiers: number[]) {
    state.disabledModifiers = disabledModifiers;
  }

  getDisabledModifiers(modifiers: DisplayedModifier[]) {
    return modifiers.reduce((acc, modifier, index) => {
      if (!modifier.inStock) {
        acc = acc.concat(index);
      }
      return acc;
    }, [] as number[]);
  }

  initPreSelectedModifiers(modifierGroups?: RepeaterModifierGroupListItem[]) {
    modifierGroups?.forEach(({ id, modifiers, rule }) => {
      if (modifiers) {
        const isMultiSelectItem = !isSingleSelectRule(rule ?? {});

        if (isMultiSelectItem) {
          const preSelectedModifiers = getPreSelectedModifiers(modifiers);
          if (preSelectedModifiers.length) {
            this.setSelectedModifiers(id, preSelectedModifiers);
          }
        } else {
          const preSelectedModifier = getFirstPreSelectedModifier(modifiers);

          if (preSelectedModifier) {
            this.setSelectedModifiers(id, [preSelectedModifier]);
          }
        }
      }
    });
  }

  initModifiers(modifierGroups: RepeaterModifierGroupListItem[]) {
    this.initPreSelectedModifiers(modifierGroups);

    this.$bindAll({
      [ITEM_MODAL_COMPONENT_IDS.modifiersContainer]: {
        // @ts-expect-error
        data: () =>
          modifierGroups.map((modifierRepeater) => ({
            _id: modifierRepeater.id,
            ...modifierRepeater,
          })) || [],
        // @ts-expect-error
        item: (modifierRepeaterData: ModifierRepeaterData, bindItem) => {
          const isMultiSelectItem = !isSingleSelectRule(modifierRepeaterData.rule ?? {});
          const modifiersOptions = getModifiersOptions(
            modifierRepeaterData.modifiers ?? [],
            this.formatCurrency,
            this.t
          );
          const isRequiredGroup = modifierRepeaterData.rule?.required;
          const disabledModifiers = this.getDisabledModifiers(modifierRepeaterData.modifiers);
          this.setDisabledModifiers(disabledModifiers);

          bindItem(ITEM_MODAL_COMPONENT_IDS.multiSelect, {
            onChange: () => {
              state.modifiersErrors[modifierRepeaterData.id] = false;
            },
          });
          if (isMultiSelectItem) {
            bindItem(ITEM_MODAL_COMPONENT_IDS.modifiersMultiState, {
              currentState: () => MODIFIER_RULE_STATES.multiSelect,
            });
            bindItem(ITEM_MODAL_COMPONENT_IDS.modifiersError, {
              collapsed: () => !state.modifiersErrors[modifierRepeaterData.id],
              hidden: () => !state.modifiersErrors[modifierRepeaterData.id],
              accessibility: {
                role: () => 'alert',
              },
            });
            bindItem(ITEM_MODAL_COMPONENT_IDS.modifiersErrorText, {
              text: () => getModifierGroupErrorText(this.t, modifierRepeaterData),
            });
          } else {
            bindItem(ITEM_MODAL_COMPONENT_IDS.modifiersSingleError, {
              collapsed: () => !state.modifiersErrors[modifierRepeaterData.id],
              hidden: () => !state.modifiersErrors[modifierRepeaterData.id],
              accessibility: {
                role: () => 'alert',
              },
            });
            bindItem(ITEM_MODAL_COMPONENT_IDS.modifiersSingleErrorText, {
              text: () => getModifierGroupErrorText(this.t, modifierRepeaterData),
            });
          }

          if (this.isEditor) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.$w(ITEM_MODAL_COMPONENT_IDS.modifiersError) as unknown as any)?.delete();
          }

          if (isMultiSelectItem) {
            bindItem(ITEM_MODAL_COMPONENT_IDS.multiSelect, {
              options: () => modifiersOptions,
              required: () => !!isRequiredGroup,
              label: () => getModifierGroupLabel(this.t, modifierRepeaterData),
              onChange: (event: $w.Event) => {
                const modifierListItems = event.target.value.map((value: string) =>
                  getModifierListItem(modifierRepeaterData?.modifiers || [], value)
                );

                this.setSelectedModifiers(modifierRepeaterData?.id, modifierListItems);
                const updatedDisabledModifiers = this.getDisabledModifiers(
                  modifierRepeaterData.modifiers
                );
                this.setDisabledModifiers(updatedDisabledModifiers);
              },
              value: () => state.selectedModifiers[modifierRepeaterData.id]?.map(({ id }) => id!),
              max: () => modifierRepeaterData.rule?.maxSelections ?? undefined,
              min: () => modifierRepeaterData.rule?.minSelections,
              disabledItemsByIndices: () => state.disabledModifiers,
              // NOTE: this is not supported yet
              accessibility: {
                ariaAttributes: {
                  describedBy: () =>
                    state.modifiersErrors[modifierRepeaterData.id]
                      ? this.$w(ITEM_MODAL_COMPONENT_IDS.modifiersError)
                      : undefined,
                },
              },
            });
          } else {
            bindItem(ITEM_MODAL_COMPONENT_IDS.singleSelect, {
              options: () => modifiersOptions,
              required: () => !!isRequiredGroup,
              label: () => getModifierGroupLabel(this.t, modifierRepeaterData),
              onChange: (event: $w.Event) => {
                const modifierListItem = getModifierListItem(
                  modifierRepeaterData?.modifiers || [],
                  event.target.value
                );

                if (modifierListItem) {
                  this.setSelectedModifiers(modifierRepeaterData?.id, [modifierListItem]);
                }

                const updatedDisabledModifiers = this.getDisabledModifiers(
                  modifierRepeaterData.modifiers
                );
                this.setDisabledModifiers(updatedDisabledModifiers);
              },
              value: () => state.selectedModifiers[modifierRepeaterData.id]?.at(0)?.id!,
              disabledItemsByIndices: () => state.disabledModifiers,
              // NOTE: this is not supported yet
              accessibility: {
                ariaAttributes: {
                  describedBy: () =>
                    state.modifiersErrors[modifierRepeaterData.id]
                      ? this.$w(ITEM_MODAL_COMPONENT_IDS.modifiersSingleError)
                      : undefined,
                },
              },
            });
          }
        },
      },
    });

    this.resetModifiersElementValidationStatus();
  }

  getPriceVariantOptions(selectedPriceVariant: DisplayedPriceVariant) {
    return selectedPriceVariant?.id
      ? ({
          id: state.selectedPriceVariant.id,
          formattedPrice: this.formatCurrency(Number(state.selectedPriceVariant.priceInfo.price)),
        } as CatalogReferencePriceVariant)
      : undefined;
  }

  validateSelectedModifierGroups(modifierGroups: RepeaterModifierGroupListItem[]) {
    const { isValid, errorsState } = isSelectedModifierGroupsValid(
      state.selectedModifiers,
      modifierGroups
    );

    state.modifiersErrors = errorsState;

    return isValid;
  }

  resetModifiersElementValidationStatus() {
    // @ts-expect-error
    this.$w(ITEM_MODAL_COMPONENT_IDS.modifiersContainer)?.onItemReady(
      // @ts-expect-error
      ($item, modifierRepeaterData: ModifierRepeaterData) => {
        const isMultiSelectItem = !isSingleSelectRule(modifierRepeaterData.rule ?? {});
        const selectionComponentId = isMultiSelectItem
          ? ITEM_MODAL_COMPONENT_IDS.multiSelect
          : ITEM_MODAL_COMPONENT_IDS.singleSelect;

        $item(selectionComponentId).resetValidityIndication();
      }
    );
  }

  updateModifiersElementValidationStatus() {
    // @ts-expect-error
    this.$w(ITEM_MODAL_COMPONENT_IDS.modifiersContainer)?.forEachItem(
      // @ts-expect-error
      ($item, modifierRepeaterData: ModifierRepeaterData) => {
        const isMultiSelectItem = !isSingleSelectRule(modifierRepeaterData.rule ?? {});
        const selectionComponentId = isMultiSelectItem
          ? ITEM_MODAL_COMPONENT_IDS.multiSelect
          : ITEM_MODAL_COMPONENT_IDS.singleSelect;

        $item(selectionComponentId).updateValidityIndication();
      }
    );
  }
}
